import baseQuery from '../../baseQuery/basePlatformQuery';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    GetTenantsResponse,
    Product,
    Tenant,
    TenantAvailableDatapoints,
    TenantInfoBySubdomain,
    TenantShortInfo,
} from './types';

const BASE_PREFIX = '/api/v1/admin/tenant';
const BASE_TENANT_PREFIX = '/api/v1/tenant';

export const tenantsApi = createApi({
    reducerPath: 'tenantsApi',
    baseQuery,
    tagTypes: ['tenants'],
    endpoints: (builder) => ({
        getTenants: builder.query<GetTenantsResponse, string>({
            query: (query) => ({
                url: `${BASE_PREFIX}?${query}`,
            }),
            providesTags: ['tenants'],
        }),
        getTenantBySubdomain: builder.query<TenantInfoBySubdomain, string>({
            query: (query) => ({
                url: `${BASE_TENANT_PREFIX}/${query}`,
            }),
        }),
        getTenant: builder.query<Tenant, number>({
            query: (id) => ({
                url: `${BASE_PREFIX}/${id}`,
            }),
        }),
        getTenantLogo: builder.query<{ logoId: string; logoDataUriContent: string }, number>({
            query: (id) => ({
                url: `${BASE_PREFIX}/${id}/logo`,
            }),
        }),
        getUserTenantLogo: builder.query<{ logoId: string; logoDataUriContent: string }, void>({
            query: () => ({
                url: `${BASE_TENANT_PREFIX}/logo`,
            }),
        }),
        getProductsByTenant: builder.query<Array<Product>, number>({
            query: (id) => ({
                url: `${BASE_PREFIX}/${id}/products`,
            }),
        }),
        getUserTenant: builder.query<Tenant, void>({
            query: () => ({
                url: BASE_TENANT_PREFIX,
            }),
        }),
        getTenantAvailableDatapoints: builder.query<TenantAvailableDatapoints, void>({
            query: () => ({
                url: `${BASE_TENANT_PREFIX}/availabledatapoints`,
            }),
        }),
        createTenant: builder.mutation<Tenant, Partial<Tenant>>({
            query: (tenant) => ({
                url: `${BASE_PREFIX}`,
                method: 'POST',
                body: tenant,
            }),
            invalidatesTags: ['tenants'],
        }),
        updateTenant: builder.mutation<Tenant, Partial<Tenant>>({
            query: (tenant) => ({
                url: `${BASE_PREFIX}/${tenant.id}`,
                method: 'PUT',
                body: tenant,
            }),
            invalidatesTags: ['tenants'],
        }),
        uploadLogo: builder.mutation<{ logoId: string; logoDataUriContent: string }, FormData>({
            query: (image) => ({
                url: `${BASE_PREFIX}/logo`,
                method: 'POST',
                body: image,
            }),
        }),
        deleteTenant: builder.mutation<Tenant, number>({
            query: (payload) => ({
                url: `${BASE_PREFIX}/${payload}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['tenants'],
        }),
        getTenantInfo: builder.query<TenantShortInfo, void>({
            query: () => ({
                url: `${BASE_TENANT_PREFIX}/style`,
            }),
            providesTags: ['tenants'],
        }),
    }),
});

export const {
    useGetTenantsQuery,
    useGetTenantBySubdomainQuery,
    useGetTenantQuery,
    useGetUserTenantLogoQuery,
    useGetTenantLogoQuery,
    useGetProductsByTenantQuery,
    useGetUserTenantQuery,
    useGetTenantAvailableDatapointsQuery,
    useCreateTenantMutation,
    useUpdateTenantMutation,
    useUploadLogoMutation,
    useDeleteTenantMutation,
    useGetTenantInfoQuery,
} = tenantsApi;
